import { inject, Injectable } from '@angular/core';
import { AsappChatService } from './asapp-chat.service';
import { AsappChatConfig, AsappCustomerInfo } from '../../model/asapp-chat/asapp-chat.dto';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { ADPResourceService } from 'digital-core';
import { ChatButtonService } from './chat-button.service';

/**
 * Service to initialize ASAPP Chat in the app component
 */
@Injectable({
  providedIn: 'root',
})
export class AsappChatInitializer {
  private asappChatService = inject(AsappChatService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private resourceService = inject(ADPResourceService);
  private chatButtonService = inject(ChatButtonService);

  private static readonly MAX_LOAD_ATTEMPTS = 3;

  /**
   * Initializes ASAPP Chat with configuration from window object
   */
  async initialize(customerInfo: AsappCustomerInfo | null): Promise<void> {

    await this.asappChatService.ensureAsappWrapperLoaded();

    const isAuthenticated = customerInfo != null;
    const siteSettings = this.buildSiteSettingsForAsapp(isAuthenticated);
    const onShow = () => this.onAsappChatShown();
    const onHide = () => this.onAsappChatHidden();

    if (!this.asappChatService.isInitialized && typeof (window as any).setupAsappChat === 'function') {
      (window as any).setupAsappChat(siteSettings, onShow, onHide);
    }

    // Check if ASAPP Chat is enabled and configuration is available
    if (this.asappChatService.isShow && !this.asappChatService.isInitialized) {
      let loaded = false;

      for (let attempt = 1; attempt <= AsappChatInitializer.MAX_LOAD_ATTEMPTS; attempt++) {
        if (attempt > 1) {
          // Exponential backoff: 2s, 4s, ...
          await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 2)));

          // Re-invoke setup to reset IsInitialized and re-trigger SDK loading
          // (wrapper script is already in the DOM; setupAsappChat will call
          //  AsappChatLoad directly via the ASAPP-already-defined branch)
          if (typeof (window as any).setupAsappChat === 'function') {
            (window as any).setupAsappChat(siteSettings, onShow, onHide);
          }
        }

        const asappConfig = window.config.Asapp as AsappChatConfig;
        this.asappChatService.load(asappConfig, this.googleAnalyticsService);
        loaded = await this.waitForAsappLoadComplete();

        if (loaded) {
          break;
        }
      }

      if (!loaded) {
        //when all 3 attempts fail, log to console so it's observable in Datadog/browser console
        console.warn(`ASAPP Chat failed to initialize after ${AsappChatInitializer.MAX_LOAD_ATTEMPTS} attempts`);
      }

      if (loaded) {
        if (customerInfo) {
          this.asappChatService.setCustomerDetail(customerInfo);
        }
        this.chatButtonService.setAsappChatInitialized(true);
      }
    } else if (!this.asappChatService.isShow && !this.asappChatService.isInitialized) {
      // ASAPP Chat is disabled or configuration is missing
      //console.log('ASAPP Chat is disabled or configuration is missing');
    }
  }


  private buildSiteSettingsForAsapp(isAuthenticated: boolean): any {
    const get = (k: string) => this.resourceService.getPortalSetting(k)?.Value ?? '';

    return {
      portalSettings: {
        Global_AsappChat_Show:      { Value: get('Global_AsappChat_Show') },
        Global_AsappChat_SDKUrl:    { Value: get('Global_AsappChat_SDKUrl') },
        Global_AsappChat_ApiHostName: { Value: get('Global_AsappChat_ApiHostName') },
        Global_AsappChat_AppId:     { Value: get('Global_AsappChat_AppId') },
        Global_AsappChat_Identity:  { Value: get('Global_AsappChat_Identity') },
        Global_AsappChat_CompanySubDivision: { Value: get('Global_AsappChat_CompanySubDivision') },
      },
      isAuthenticated: isAuthenticated,
    };
  }

  private waitForAsappLoadComplete(timeoutMs = 30000): Promise<boolean> {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const ready = !!(window as any).config?.Asapp && (window as any).config.Asapp.IsInitialized === true;
        if (ready) {
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  private onAsappChatShown(): void {
    this.chatButtonService.setAsappChatVisibility(true);
  }

  private onAsappChatHidden(): void {
    this.chatButtonService.setAsappChatVisibility(false);
  }
}
